import { _js } from '@ifixit/utils/localize';
import { cn } from '@ifixit/ui/lib/utils';
import { useState } from 'react';

/**
 * The `n/max` character count under a step line, shared by the guide editor
 * and the translate page so the two cannot drift. Turns red and bold past
 * the limit; a polite live region says so, and says when the line is back
 * under it, since the count itself is only read on request. The live span
 * is always mounted (a region has to exist before its text changes) and
 * stays `visible` so a parent that hides the count with `invisible` while
 * idle does not hide the announcement with it.
 */
export function LineCount({
   length,
   max,
   className,
}: {
   length: number;
   max: number;
   className?: string;
}) {
   const isOver = length > max;
   // Only a line that has been over gets the "under again" note: a fresh
   // row is under the limit too and has nothing to announce.
   const [wasOver, setWasOver] = useState(false);
   if (isOver && !wasOver) {
      setWasOver(true);
   }
   const note = isOver
      ? _js('This line is over the character limit.')
      : wasOver
        ? _js('This line is under the character limit again.')
        : '';
   return (
      <span
         className={cn(
            'shrink-0 text-xs tabular-nums',
            isOver ? 'font-semibold text-destructive' : 'text-muted-foreground',
            className
         )}
      >
         <span aria-live="polite" className="sr-only visible">
            {note}
         </span>
         {length}/{max}
      </span>
   );
}
